/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useForm } from "react-hook-form";
import { Button } from "@nordcloud/gnui";
import { SidebarMultiSelect } from "~/components/SidebarForm/inputs/SidebarMultiSelect";
import { SidebarTextField } from "~/components/SidebarForm/inputs/SidebarTextField";
import { SidebarForm } from "~/components/SidebarForm/SidebarForm";
import { SidebarFormButtons } from "~/components/SidebarForm/SidebarFormButtons";
import { FCPatchPlan } from "~/models";
import { ACTIONS, Can } from "~/routing";

type Props = {
  environments: string[];
  addPlan: (newPlan: FCPatchPlan) => Promise<void>;
};

function FCPatchPlanAdd({ environments, addPlan }: Props) {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  const formMethods = useForm();
  const watcher = formMethods.watch();

  const onSubmit = async (values: FCPatchPlan) => {
    setIsLoading(true);
    try {
      await addPlan(values);
    } finally {
      setIsLoading(false);
      setIsSidebarOpen(false);
    }
  };

  return (
    <>
      <Can action={ACTIONS.EDIT_APPLICATION}>
        <Button
          size="md"
          css={{ marginLeft: "auto", marginBottom: "1rem" }}
          icon="plus"
          onClick={() => setIsSidebarOpen(true)}
        >
          Add plan
        </Button>
      </Can>

      <SidebarForm
        title="Add Fleet Control patching plan"
        open={isSidebarOpen}
        setOpen={setIsSidebarOpen}
        formMethods={formMethods}
        submit={onSubmit}
      >
        <SidebarTextField
          name="customerName"
          label="Customer name"
          placeholder="Customer name"
          required
        />

        <SidebarTextField
          name="plan"
          label="Plan link"
          placeholder="Plan link"
          required
          registerOptions={{
            pattern: {
              value: /^https:\/\/(fleetcontrol\.[\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/,
              message:
                "Please provide a correct Fleet Control link (starting with https://fleetcontrol)\nExample: https://fleetcontrol.nordcloudapp.com/plans/123",
            },
          }}
        />

        <SidebarMultiSelect
          name="environments"
          label="Environments"
          options={environments}
          required
        />

        <SidebarTextField name="cron" label="Schedule (Cron)" required />

        <SidebarFormButtons
          onCancel={() => setIsSidebarOpen(false)}
          submitText="Add"
          submitLoading={isLoading}
          submitEnabled={
            !!(
              watcher.customerName &&
              watcher.plan &&
              watcher.environments?.length &&
              watcher.cron
            )
          }
        />
      </SidebarForm>
    </>
  );
}

export { FCPatchPlanAdd };
